//
// # Background Grid
//
// Dynamic decade-based adaptive grid. On each frame, the grid computes which
// lines are visible from the current viewport and zoom scale. Two tiers of
// lines (minor and major) are drawn, with the tier spacing shifting as the
// user zooms: e.g. (10, 100) -> (100, 1000) -> (1000, 10000).
//
// Minor lines fade in/out smoothly via alpha as the zoom level transitions
// between decades. Line widths are constant in screen pixels (~1px minor,
// ~1.5px major) regardless of zoom level.
//

import { graphics, Point, Bbox } from "GenAlphaModel";
// Local Imports
import { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";
import type { RedrawRequest } from "DrawCore/src/redraw";
import type { InteractionHost } from "./interaction_host";

interface Settings {
  snap?: number; // Snap to grid size. Positive integer. All positions are snapped to multiples of this value. Default is 10.
  snapX?: number; // Snap to grid size for X axis. Overrides `snap` for X when set. Used for layout with PDK-specific grids.
  snapY?: number; // Snap to grid size for Y axis. Overrides `snap` for Y when set. Used for layout with PDK-specific grids.
  minor?: number; // Minor grid size. Positive integer. The grid is drawn every `minor` pixels. Default is 10.
  multiple?: number; // Multiple of `minor` to draw major lines. Integer with a minimum value of two. Default is 10.
}

class ValidSettings {
  //
  readonly snap: number;
  readonly snapX: number; // Effective snap for X axis
  readonly snapY: number; // Effective snap for Y axis
  readonly minor: number;
  readonly multiple: number;
  //
  constructor(arg: Settings) {
    this.snap = arg.snap ? Math.round(Math.max(arg.snap, 1)) : 10;
    // snapX/snapY override snap when set, otherwise fall back to snap
    this.snapX = arg.snapX ? Math.round(Math.max(arg.snapX, 1)) : this.snap;
    this.snapY = arg.snapY ? Math.round(Math.max(arg.snapY, 1)) : this.snap;
    this.minor = arg.minor ? Math.round(Math.max(arg.minor, 1)) : 10;
    this.multiple = arg.multiple ? Math.round(Math.max(arg.multiple, 2)) : 10;
  }
  // Return as a "regular" `Settings` object
  get settings(): Settings {
    const { snap, snapX, snapY, minor, multiple } = this;
    return { snap, snapX, snapY, minor, multiple };
  }
}

const DEFAULT_SETTINGS = new ValidSettings({
  snap: 10,
  minor: 10,
  multiple: 10,
});

// Minimum screen-pixel spacing between minor grid lines before the grid
// shifts to a coarser decade tier.
const MIN_SCREEN_SPACING = 15;

export class Grid {
  //
  visible: boolean = true; // Grid visibility, toggled with Shift+G
  highIntensity: boolean = false; // Grid line intensity (high = bolder lines)

  private constructor(
    readonly editor: InteractionHost,
    public settings: ValidSettings // Settings
  ) {}

  static empty(
    editor: InteractionHost,
    settings: ValidSettings = DEFAULT_SETTINGS
  ): Grid {
    return new Grid(editor, settings);
  }

  toggle = (): RedrawRequest => {
    this.visible = !this.visible;
    return this.editor.requestRedraw();
  };

  setHighIntensity = (high: boolean): RedrawRequest => {
    this.highIntensity = high;
    return this.editor.requestRedraw();
  };

  // Draw the grid for the current viewport and zoom level.
  // Computes visible lines from scratch each frame -- no stored state needed.
  redraw = (to: DrawTo): unknown => {
    if (!this.visible) return null;

    const { settings } = this;
    const { minor, multiple } = settings;

    // Get the current viewport in schematic coordinates
    const canvas = this.editor.canvas;
    const { scale } = canvas.worldTransforms.schToPage;

    // Guard against degenerate scale (extremely zoomed out or uninitialized)
    if (scale <= 0) return null;

    const { width, height } = canvas.size;
    const pageBbox = { top: 0, bottom: height, left: 0, right: width };
    const viewport = canvas.worldTransforms.toSchBbox(pageBbox);
    const { min, max } = viewport;

    // Decade-based tier selection:
    // Find the smallest n where minor * multiple^n * scale >= MIN_SCREEN_SPACING
    const rawN =
      Math.log(MIN_SCREEN_SPACING / (minor * scale)) / Math.log(multiple);
    const n = Math.max(0, Math.ceil(rawN));

    // Fade factor: 0 = minor lines about to vanish, 1 = fully visible
    // When n = 0, we're at the finest tier, minor lines are always fully visible
    const t = n > 0 ? n - rawN : 1;

    const minorSpacing = minor * Math.pow(multiple, n);
    const majorSpacing = minorSpacing * multiple;

    // Constant screen-pixel line widths (converted to schematic coordinates)
    const minorLineWidth = 1.0 / scale;
    const majorLineWidth = 1.5 / scale;

    // Get theme-appropriate base color and pre-compute styles for this frame
    const baseColor = gridBaseColor(
      style.Theme.isDark(style.Theme.get()),
      this.highIntensity
    );
    const minorAlpha = Math.max(0.05, t);
    const minorStyle = makeGridLineStyle(baseColor, minorAlpha, minorLineWidth);
    const majorStyle = makeGridLineStyle(baseColor, 1.0, majorLineWidth);

    // Compute line range: pad to nearest minor grid boundaries
    const xStart = Math.floor(min.x / minorSpacing) * minorSpacing;
    const xEnd = Math.ceil(max.x / minorSpacing) * minorSpacing;
    const yStart = Math.floor(min.y / minorSpacing) * minorSpacing;
    const yEnd = Math.ceil(max.y / minorSpacing) * minorSpacing;

    // Draw vertical lines
    for (let x = xStart; x <= xEnd; x += minorSpacing) {
      const isMajor = Math.abs(x % majorSpacing) < 0.5;
      const path = graphics.Path.fromPartial({
        points: [
          { x, y: min.y },
          { x, y: max.y },
        ],
      });
      to.path(path, isMajor ? majorStyle : minorStyle);
    }

    // Draw horizontal lines
    for (let y = yStart; y <= yEnd; y += minorSpacing) {
      const isMajor = Math.abs(y % majorSpacing) < 0.5;
      const path = graphics.Path.fromPartial({
        points: [
          { x: min.x, y },
          { x: max.x, y },
        ],
      });
      to.path(path, isMajor ? majorStyle : minorStyle);
    }

    return null;
  };

  updateSettings = (settings: Settings): RedrawRequest => {
    this.settings = new ValidSettings(settings);
    return this.editor.requestRedraw();
  };

  // Get a `Bbox` padded to the grid's major lines.
  static padBbox = (
    bbox: Bbox,
    settings: ValidSettings = DEFAULT_SETTINGS
  ): Bbox => {
    const majorSize = settings.minor * settings.multiple;
    const { min, max } = bbox;
    return {
      min: {
        x: Math.floor(min.x / majorSize) * majorSize,
        y: Math.floor(min.y / majorSize) * majorSize,
      },
      max: {
        x: Math.ceil(max.x / majorSize) * majorSize,
        y: Math.ceil(max.y / majorSize) * majorSize,
      },
    };
  };

  // Given a `Point`, return the nearest grid point.
  // Uses snapX/snapY for independent x/y snapping (e.g., PDK-specific layout grids).
  nearestPoint = (loc: Point): Point => {
    const { snapX, snapY } = this.settings;
    return Point.new(
      Math.round(loc.x / snapX) * snapX,
      Math.round(loc.y / snapY) * snapY
    );
  };

  // Find the nearest Manhattan-separated point on the grid relative to `relativeTo`.
  nearestManhattan = (loc: Point, relativeTo: Point): Point => {
    const diff = Point.sub(relativeTo, loc);
    if (Math.abs(diff.x) > Math.abs(diff.y)) {
      // Horizontal segment
      return this.nearestPoint(Point.new(loc.x, relativeTo.y));
    } else {
      // Vertical segment
      return this.nearestPoint(Point.new(relativeTo.x, loc.y));
    }
  };
}

// Pick the base grid color for a given theme and intensity setting.
// "High intensity" uses bolder lines (the old high-contrast colors);
// "normal" uses subtler lines (the old defaults).  The four palette
// entries live in style/color.ts alongside the other colour primitives.
function gridBaseColor(isDark: boolean, highIntensity: boolean): style.Color {
  if (isDark) {
    return highIntensity
      ? style.Color.gridLine.darkBold
      : style.Color.gridLine.darkSubtle;
  } else {
    return highIntensity
      ? style.Color.gridLine.lightBold
      : style.Color.gridLine.lightSubtle;
  }
}

// Create a grid line style with the given base color, alpha, and line width.
// Alpha is clamped to a minimum of 0.01 to work around a Color.str truthiness
// issue where a=0 produces opaque rgb() instead of transparent rgba().
function makeGridLineStyle(
  base: style.Color,
  alpha: number,
  linewidth: number
): style.ShapeStyle {
  const color = style.Color.new(base.r, base.g, base.b, Math.max(alpha, 0.01));
  return {
    linewidth,
    cap: "butt",
    join: "miter",
    fill: color,
    stroke: color,
  };
}
